import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Grid, Button, TextField, InputAdornment } from '@mui/material';
import { MdSearch } from 'react-icons/md';
import { DataGrid } from '@mui/x-data-grid';
import TopBar from '../components/topbar';
import SidebarNav from '../components/Sidebar';
import PlayerDetail from '../components/PlayerDetail';
import { dataGridSx } from '../utils/dataGridTheme';
import '../App.css';

const panelStyle = {
    background: 'rgba(255, 255, 255, 0.23)',
    border: '2px solid rgba(120, 53, 15, 0.6)',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
};

const searchFieldSx = {
    '& .MuiInputLabel-root': { color: 'rgba(254, 243, 199, 0.7)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#f59e0b' },
    '& .MuiOutlinedInput-root': {
        color: '#ffffff',
        backgroundColor: 'rgba(20, 15, 10, 0.75)',
        borderRadius: '8px',
        '& fieldset': { borderColor: 'rgba(245, 158, 11, 0.5)' },
        '&:hover fieldset': { borderColor: 'rgba(245, 158, 11, 0.8)' },
        '&.Mui-focused fieldset': { borderColor: '#f59e0b' },
    },
};

const AdminDashboard = () => {
    const [users, setUsers] = useState([]);
    const [auditLog, setAuditLog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIdentifier, setSelectedIdentifier] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const columns = [
        { field: 'character_names', headerName: 'Name', width: 200 },
        { field: 'identifier', headerName: 'Identifier', width: 220 },
        { field: 'group', headerName: 'Group', width: 130 },
        { field: 'warnings', headerName: 'Warnings', width: 110 },
        { field: 'banned', headerName: 'Banned', width: 100, valueFormatter: (value) => value ? 'Yes' : 'No' },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 130,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => setSelectedIdentifier(params.row.identifier)}
                >
                    View / Edit
                </Button>
            ),
        },
    ];

    const auditColumns = [
        { field: 'created_at', headerName: 'When', width: 180 },
        { field: 'actor_identifier', headerName: 'Actor', width: 200 },
        { field: 'action', headerName: 'Action', width: 150 },
        { field: 'target_id', headerName: 'Target', width: 200 },
        { field: 'notes', headerName: 'Notes', width: 200 },
    ];

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/admin/users`, {
                credentials: 'include'
            });
            const result = await response.json();
            setUsers(result.map((user) => ({ id: user.identifier, ...user })));
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAuditLog = async () => {
        try {
            const response = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/admin/audit-log`, {
                credentials: 'include'
            });
            const result = await response.json();
            setAuditLog(result.map((entry) => ({ id: entry.id, ...entry })));
        } catch (error) {
            console.error('Error fetching audit log:', error);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchAuditLog();
    }, []);

    const filteredUsers = users.filter((user) => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return true;
        return [user.character_names, user.identifier, user.group]
            .some((field) => (field || '').toLowerCase().includes(query));
    });

    return (
        <div className="businessgrid">
            <div className="topNav">
                <TopBar />
            </div>
            <div className="sidebar-container">
                <SidebarNav />
            </div>
            <div className='test'>
                <h1 style={{ textAlign: 'center', color: 'white', fontFamily: 'Playfair Display, serif', fontSize: '50px', marginBottom: '0px' }}>Admin Dashboard</h1>
                <p style={{ textAlign: 'center', color: 'rgba(254, 243, 199, 0.7)', fontFamily: 'serif', fontSize: '20px', marginTop: '0px' }}>System Management</p>

                <div style={{ padding: '20px' }}>
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={panelStyle}>
                                <CardContent>
                                    <Typography sx={{ color: 'rgba(254, 243, 199, 0.7)', fontFamily: 'serif' }} gutterBottom>
                                        Total Users
                                    </Typography>
                                    <Typography variant="h4" sx={{ color: '#ffffff', fontFamily: 'serif' }}>{users.length}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Card sx={{ ...panelStyle, mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, color: '#ffffff', fontFamily: 'serif' }}>
                                User Management
                            </Typography>
                            <TextField
                                placeholder="Search by name, identifier, or group..."
                                fullWidth
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{ ...searchFieldSx, mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <MdSearch color="#f59e0b" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <div style={{ height: 400, width: '100%' }}>
                                <DataGrid
                                    rows={filteredUsers}
                                    columns={columns}
                                    pageSize={5}
                                    rowsPerPageOptions={[5]}
                                    loading={loading}
                                    sx={dataGridSx}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card sx={panelStyle}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, color: '#ffffff', fontFamily: 'serif' }}>
                                Audit Log
                            </Typography>
                            <div style={{ height: 400, width: '100%' }}>
                                <DataGrid
                                    rows={auditLog}
                                    columns={auditColumns}
                                    pageSize={5}
                                    rowsPerPageOptions={[5]}
                                    sx={dataGridSx}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <PlayerDetail
                open={!!selectedIdentifier}
                identifier={selectedIdentifier}
                onClose={() => setSelectedIdentifier(null)}
                onSaved={() => { fetchUsers(); fetchAuditLog(); }}
            />
        </div>
    );
};

export default AdminDashboard;
